import { FEATURES } from '@constants/Text'
import {
  ORIGEM_INTELLIGENCE_V2,
  PERSONAS,
  PERSONA_CONSULTOR_ESTRATEGICO,
  PERSONA_ESPECIALISTA_FEE_FIXO,
  PERSONA_IA_OPERACIONAL,
} from '@modules/aawz-intelligence-v2/constants/Personas'

/**
 * Catálogo de agentes do chat IA — o eixo VISÍVEL da UI depois do redesign.
 *
 * Agente NÃO é persona. `persona` é quem responde no backend (o valor gravado
 * na coluna `context`); agente é a entrada que o usuário escolhe no composer e
 * na sidebar. Dois agentes podem apontar para a MESMA persona: Painel e
 * Consultor usam `CONSULTOR_ESTRATEGICO` — o que muda entre eles é a pergunta
 * que a UI induz, não o endpoint.
 *
 * O catálogo é data-driven de propósito (AC-4.5): acrescentar um dos 7
 * especialistas é acrescentar uma entrada aqui, sem tocar em componente. Ver
 * gap G1 do REQUIREMENTS — hoje o backend não tem persona por especialista.
 *
 * ★ `iconName` só aceita nome existente em `src/components/icons/svg/`: nome
 * inexistente não dá erro, o ícone simplesmente não desenha. `Agentes.unit.js`
 * trava isso lendo o diretório.
 */

export const AGENTE_PAINEL = 'painel'
export const AGENTE_CONSULTOR = 'consultor'
export const AGENTE_OPERACIONAL = 'operacional'
export const AGENTE_FEE_FIXO = 'fee-fixo'

// Default da rota /aawz-intelligence/chat-ia — é o comportamento de hoje.
export const AGENTE_DEFAULT_ID = AGENTE_OPERACIONAL

// Congelado: `agentesVisiveis` devolve as MESMAS referências do catálogo, e um
// consumidor que escrevesse em `agente.label` corromperia o módulo pro app inteiro.
export const AGENTES = [
  {
    id: AGENTE_PAINEL,
    persona: PERSONA_CONSULTOR_ESTRATEGICO,
    // O único `false` do catálogo: compartilha a persona com o Consultor e
    // perde a via inversa para ele. Ver `agenteIdPorPersona`.
    principalDaPersona: false,
    // ★ [06/09/2026] `agentSlug: null` — Painel não é um agente do
    // Supervisor, é um MODO (`convocar_painel` é tool que o modelo decide
    // chamar). Não há `agent_slug` que o represente; ver
    // `agentSlugRequisitavel`. Declarado (não omitido) para o contrato dos
    // campos do catálogo continuar uniforme (ver o teste "12 campos").
    agentSlug: null,
    label: 'Painel multi-agente',
    shortName: 'Multi-agente',
    description: 'Várias áreas do escritório olhando o mesmo caso.',
    // ★ O Painel é o único com identidade própria: leva a MARCA (`ia-w-mark`).
    // Os outros três compartilham `robot`, como no protótipo — o que distingue
    // um agente do outro na lista é o rótulo, não o glifo.
    iconName: 'ia-w-mark',
    featureFlag: FEATURES.SOLICITACOES,
    origemModulo: ORIGEM_INTELLIGENCE_V2,
    // Mesmo placeholder do Consultor: `convocar_painel` é uma tool que o MODELO
    // decide chamar, então esta entrada induz o painel pela pergunta e não
    // promete que ele vai rodar (gap G1-b).
    placeholder: PERSONAS[PERSONA_CONSULTOR_ESTRATEGICO].placeholder,
    headline: 'Como podemos te ajudar hoje?',
  },
  {
    id: AGENTE_CONSULTOR,
    persona: PERSONA_CONSULTOR_ESTRATEGICO,
    // Representa `CONSULTOR_ESTRATEGICO` na via inversa: o Painel compartilha a
    // persona, mas é um MODO dela (convoca o comitê), não a identidade dela. Uma
    // conversa reaberta pelo histórico volta como Consultor.
    principalDaPersona: true,
    // ★ [06/09/2026] `agentSlug: null` — o sucessor real (`strategic-advisor`)
    // é `classe: advisory` no registry do backend: o guard de elegibilidade
    // (D13) recusa qualquer coisa que não seja `operational` para chat
    // humano. Advisors só entram pelo Advisory Hub (§5.6 do CLAUDE.md do
    // módulo), mecanismo diferente. Ver `agentSlugRequisitavel`.
    agentSlug: null,
    label: PERSONAS[PERSONA_CONSULTOR_ESTRATEGICO].label,
    shortName: 'Consultor',
    description: 'Maturidade do escritório e catálogo de serviços AAWZ.',
    iconName: 'robot',
    featureFlag: FEATURES.SOLICITACOES,
    origemModulo: ORIGEM_INTELLIGENCE_V2,
    placeholder: PERSONAS[PERSONA_CONSULTOR_ESTRATEGICO].placeholder,
    headline: 'Vamos falar com o Consultor Estratégico?',
  },
  {
    id: AGENTE_OPERACIONAL,
    persona: PERSONA_IA_OPERACIONAL,
    // Único agente da persona — vence a via inversa por não ter concorrente.
    principalDaPersona: true,
    // ★ [06/09/2026] `agentSlug: null`, de propósito — este É o
    // `AGENTE_DEFAULT_ID`. Escolher "IA Operacional" na sidebar é escolher o
    // DEFAULT, não um agente específico: mandar `agente_solicitado` aqui
    // colapsaria "não escolhi" e "escolhi o default" no mesmo estado, que é
    // exatamente a ambiguidade que este pedido do owner existe para eliminar
    // (o sucessor real seria `client-relationship`, mas não é isso que o
    // clique em IA Operacional significa). Ver `agentSlugRequisitavel`.
    agentSlug: null,
    label: PERSONAS[PERSONA_IA_OPERACIONAL].label,
    shortName: 'Operacional',
    description: 'Clientes, tarefas, oportunidades e pipeline de vendas.',
    iconName: 'robot',
    featureFlag: FEATURES.CHAT_IA,
    origemModulo: ORIGEM_INTELLIGENCE_V2,
    // Herda a copy da persona: é a entrada default e o texto de hoje na tela.
    placeholder: PERSONAS[PERSONA_IA_OPERACIONAL].placeholder,
    headline: PERSONAS[PERSONA_IA_OPERACIONAL].headline,
  },
  {
    id: AGENTE_FEE_FIXO,
    persona: PERSONA_ESPECIALISTA_FEE_FIXO,
    // Único agente da persona — vence a via inversa por não ter concorrente.
    principalDaPersona: true,
    // ★ [06/09/2026] O ÚNICO item do catálogo com `agentSlug` — é o único
    // agente visível que coincide com um `agent_slug` real, `operational` no
    // registry do backend (`agentes/catalogo/fee-fixo.yaml`), logo roteável
    // por `agente_solicitado`. Ver `agentSlugRequisitavel`.
    agentSlug: AGENTE_FEE_FIXO,
    label: PERSONAS[PERSONA_ESPECIALISTA_FEE_FIXO].label,
    shortName: 'Fee Fixo',
    description: 'Migração de comissionado para fee fixo, cliente a cliente.',
    iconName: 'robot',
    // `FEATURES.FEE_FIXO` ('Fee Fixo') e NÃO `FEATURES.IA_FEE_FIXO`: o backend
    // gateia a persona ESPECIALISTA_FEE_FIXO em `FuncionalidadesNames.FeeFixo`
    // (`constants/personas.py`), que é o espelho desta chave.
    featureFlag: FEATURES.FEE_FIXO,
    origemModulo: ORIGEM_INTELLIGENCE_V2,
    placeholder: PERSONAS[PERSONA_ESPECIALISTA_FEE_FIXO].placeholder,
    headline: 'Vamos falar com o Especialista em Fee Fixo?',
  },
].map(Object.freeze)

// Resolvido uma vez aqui, e não a cada `getAgente`: se este `find` devolvesse
// `undefined`, o erro apareceria no import e não num campo vazio em runtime.
const AGENTE_DEFAULT = AGENTES.find((agente) => agente.id === AGENTE_DEFAULT_ID)

/**
 * Agentes liberados para o escritório, na ordem do catálogo.
 *
 * Pura: recebe o mapa `$access` em vez de ler o store. `catalogo` é parâmetro
 * para o teste provar a promessa data-driven (AC-4.5) sem mutar `AGENTES`.
 *
 * @param {Object} access Mapa `$access` — nome da funcionalidade → truthy.
 * @param {Array} catalogo Catálogo a filtrar. Default: `AGENTES`.
 * @returns {Array} Subconjunto do catálogo.
 */
export function agentesVisiveis(access, catalogo = AGENTES) {
  const liberadas = access || {}
  return catalogo.filter(
    (agente) => !agente.featureFlag || Boolean(liberadas[agente.featureFlag])
  )
}

/**
 * Resolve o agente pelo id, caindo no default para id ausente ou desconhecido.
 *
 * Cair no default (e não devolver `undefined`) é o que impede o composer de ler
 * `agente.placeholder` de nada e renderizar um campo sem rótulo em silêncio.
 *
 * @param {String} id Id do agente.
 * @returns {Object} Registro do agente.
 */
export function getAgente(id) {
  const encontrado = AGENTES.find((agente) => agente.id === id)
  return encontrado || AGENTE_DEFAULT
}

/**
 * O agente que representa uma persona.
 *
 * É a via inversa de `getAgente`: a conversa guarda a PERSONA em `context`, e o
 * histórico (que agora mistura agentes) precisa dizer de quem ela é e trocar o
 * agente ao abri-la.
 *
 * Duas entradas apontam para `CONSULTOR_ESTRATEGICO` — Painel e Consultor — e
 * do registro não dá para saber qual das duas falou. Antes vencia a primeira do
 * catálogo (`painel`), o que fazia o chip da linha dizer "Consultor" e o rodapé
 * dizer "Multi-agente" para a MESMA conversa. `principalDaPersona` desempata de
 * forma explícita, em vez de depender da ordem de apresentação — que existe
 * para o seletor, não para esta resolução.
 *
 * Persona desconhecida cai no default, e não em `undefined`, pelo mesmo motivo
 * de `getAgente`.
 *
 * @param {String} persona Persona do backend (coluna `context`).
 * @returns {String} Id do agente.
 */
export function agenteIdPorPersona(persona) {
  const daPersona = AGENTES.filter((agente) => agente.persona === persona)
  if (!daPersona.length) return AGENTE_DEFAULT_ID
  const principal = daPersona.find((agente) => agente.principalDaPersona)
  return (principal || daPersona[0]).id
}

/**
 * O `agent_slug` operacional que SUCEDE cada persona legada no registry do
 * Supervisor (`agentes/catalogo/*.yaml`, backend). Fonte: a descrição de cada
 * agente em `ui-registry/contratos/agentes-nomes.json` ("Sucessor da persona
 * ...") — usado SÓ pelo drift-guard de rótulo (`Agentes.unit.js`, F-7), que
 * confere se o `label` do agente PRINCIPAL de cada persona ainda bate com o
 * `nome` do slug sucessor.
 *
 * ⚠️ **NÃO é a fonte de `agentSlugRequisitavel`.** "Sucessor da persona" é uma
 * relação de LABEL/identidade, mais ampla que "pode ser mandado como
 * `agente_solicitado`" — `strategic-advisor` é sucessor de verdade do
 * Consultor, mas é `classe: advisory` e nunca roteável por chat humano (D13).
 * A régua de "manda ou não" mora só no catálogo (`AGENTES[].agentSlug`),
 * campo por item — nunca derivada deste mapa.
 */
export const AGENT_SLUG_POR_PERSONA = {
  [PERSONA_IA_OPERACIONAL]: 'client-relationship',
  [PERSONA_CONSULTOR_ESTRATEGICO]: 'strategic-advisor',
  [PERSONA_ESPECIALISTA_FEE_FIXO]: 'fee-fixo',
}

/**
 * O `agent_slug` a MANDAR como `agente_solicitado` no corpo do `/chat`
 * (`_buildChatBody`), a partir do agente que o usuário ESCOLHEU na
 * sidebar/palette/composer.
 *
 * ★ [06/09/2026] A régua é literal: **manda só quando o item escolhido É um
 * agente do catálogo** — `AGENTES[].agentSlug`, marcado item a item (hoje só
 * `AGENTE_FEE_FIXO` tem). Não deriva de persona nem de heurística: Painel não
 * é agente (é um MODO), Operacional é o DEFAULT (escolhê-lo é "não escolher
 * um agente específico" — colapsar isso com uma tradução automática
 * reintroduziria a ambiguidade que este pedido do owner existe para
 * eliminar), e Consultor aponta para um `advisory` que o guard de
 * elegibilidade do backend recusa sempre (D13) — os três comentários,
 * item a item no catálogo acima, explicam cada caso.
 *
 * @param {String} agenteId Id do agente do catálogo visível (`AGENTES`).
 * @returns {String|null} `agent_slug` a enviar, ou `null`.
 */
export function agentSlugRequisitavel(agenteId) {
  return getAgente(agenteId).agentSlug || null
}

/**
 * Via inversa de `agentSlugRequisitavel` — o agente da UI que representa o
 * `agent_slug` que RESPONDEU de fato, para reconciliar a sidebar quando o
 * backend recusa o pedido (guard de elegibilidade) e o turno segue pelo
 * roteador normal com outro agente (`nos.py::montar_no_route_domains`).
 *
 * Sem item do catálogo com esse `agentSlug` (a maioria dos ~9 agentes
 * operacionais do backend não tem entrada própria na sidebar — só
 * `fee-fixo`), cai no `AGENTE_DEFAULT_ID`: não inventa marca/ícone de UI para
 * um especialista sem representação própria — dizer "IA Operacional
 * respondeu" é a afirmação mais honesta possível sem forjar uma entrada que
 * não existe.
 *
 * @param {String} agentSlug Slug do agente que respondeu de fato (`_runs.agent_slug`).
 * @returns {String} Id do agente (catálogo visível), ou `AGENTE_DEFAULT_ID`.
 */
export function agenteIdPorAgentSlug(agentSlug) {
  if (!agentSlug) return AGENTE_DEFAULT_ID
  const encontrado = AGENTES.find((agente) => agente.agentSlug === agentSlug)
  return (encontrado && encontrado.id) || AGENTE_DEFAULT_ID
}
